import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DatePicker, Input, Checkbox, Button, Table, message, Card } from 'antd';
import { SearchOutlined, FileExcelOutlined } from '@ant-design/icons';
import moment from 'moment';
import * as XLSX from 'xlsx';

const { RangePicker } = DatePicker;

const XuatExcelHoaDon = () => {
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState([moment().subtract(7, 'days'), moment()]);
  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Lấy danh sách cửa hàng
  useEffect(() => {
    fetchStores();
  }, []);

  // Lọc cửa hàng theo search text
  useEffect(() => {
    if (searchText) {
      const filtered = stores.filter(store =>
        store.TenCuaHang.toLowerCase().includes(searchText.toLowerCase()) ||
        store.IDCH.toString().includes(searchText)
      );
      setFilteredStores(filtered);
    } else {
      setFilteredStores(stores);
    }
  }, [searchText, stores]);

  const fetchStores = async () => {
    try {
      const response = await axios.get('https://doticom.vn:3000/stores');
      setStores(response.data);
      setFilteredStores(response.data);
    } catch (error) {
      message.error('Không thể lấy danh sách cửa hàng');
      console.error(error);
    }
  };

  const handleStoreChange = (storeId) => {
    setSelectedStores(prev =>
      prev.includes(storeId)
        ? prev.filter(id => id !== storeId)
        : [...prev, storeId]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedStores(filteredStores.map(store => store.IDCH));
    } else {
      setSelectedStores([]);
    }
  };

  const fetchProductData = async () => {
    if (selectedStores.length === 0) {
      message.warning('Vui lòng chọn ít nhất một cửa hàng');
      return;
    }

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning('Vui lòng chọn khoảng thời gian');
      return;
    }

    setLoading(true);
    try {
      const tuNgay = dateRange[0].format('YYYY-MM-DD');
      const denNgay = dateRange[1].format('YYYY-MM-DD');
      const maCHList = selectedStores.join(',');

      const response = await axios.get(`https://doticom.vn:3000/api/tong-hop-chi-tiet-bill-all?MaCH=${maCHList}&TuNgay=${tuNgay}&DenNgay=${denNgay}`);
      
      const allData = response.data?.success ? response.data.data || [] : [];
      const flattened = allData.flatMap(store =>
        (store.ChiTiet || []).map(item => ({
          ...item,
          TenCuaHang: store.TenCuaHang,
          MaCH_CT: store.MaCH_CT,
          NgayXuat: store.NgayXuat
        }))
      );
      setProductData(flattened);
      message.success(`Đã tải ${flattened.length} bản ghi`);
    } catch (error) {
      message.error('Không thể lấy dữ liệu sản phẩm');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const excludedMaNLSP = ['NLBL', 'TH'];
    const exportRows = productData.filter(
      (item) => !excludedMaNLSP.includes(item?.MaNLSP)
    );

    if (exportRows.length === 0) {
      message.warning('Không có dữ liệu hợp lệ để xuất');
      return;
    }

    try {
      const groupedByStore = exportRows.reduce((acc, item) => {
        const storeName = item.TenCuaHang || 'Không xác định';
        const ngayXuat = item.NgayXuat || 'Không rõ';
        acc[storeName] = acc[storeName] || {};
        acc[storeName][ngayXuat] = acc[storeName][ngayXuat] || [];
        acc[storeName][ngayXuat].push(item);
        return acc;
      }, {});

      const wb = XLSX.utils.book_new();

      Object.entries(groupedByStore).forEach(([storeName, dates]) => {
        Object.entries(dates).forEach(([date, rows]) => {
          const aoa = [
            ['STT', 'MaVT', 'TenVT', 'SoLuong', 'GiaBan', 'ThanhTien', 'GiaBan_ThucTe', 'ThanhTien_ThucTe'],
            ...rows.map((item, index) => [
              index + 1,
              item.MaSP,
              item.TenSP,
              item.SoLuong,
              Math.round(item.GiaBan * 0.8),
              Math.round(item.ThanhTien * 0.8),
              item.GiaBan,
              item.ThanhTien
            ])
          ];
          const ws = XLSX.utils.aoa_to_sheet(aoa);
          ws['!cols'] = [
            { wch: 5 },
            { wch: 12 },
            { wch: 40 },
            { wch: 10 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 }
          ];
          const sheetName = `${storeName.substring(0, 20)}-${moment(date).format('DDMMYY')}`.substring(0, 31);
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        });
      });

      const fileName = `BaoCao_${dateRange[0].format('DDMMYYYY')}_${dateRange[1].format('DDMMYYYY')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      message.success(`Đã xuất file ${fileName} thành công`);
    } catch (error) {
      message.error('Có lỗi khi xuất file Excel');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Mã SP',
      dataIndex: 'MaSP',
      key: 'MaSP',
      width: 100,
    },
    {
      title: 'Tên sản phẩm',
      dataIndex: 'TenSP',
      key: 'TenSP',
      width: 300,
    },
    {
      title: 'Số lượng',
      dataIndex: 'SoLuong',
      key: 'SoLuong',
      width: 100,
      align: 'right',
    },
    {
      title: 'Giá bán',
      dataIndex: 'GiaBan',
      key: 'GiaBan',
      width: 120,
      align: 'right',
      render: (value) => value?.toLocaleString('vi-VN') + ' đ',
    },
    {
      title: 'Giảm giá %',
      dataIndex: 'GiamPT',
      key: 'GiamPT',
      width: 100,
      align: 'right',
    },
    {
      title: 'Thành tiền',
      dataIndex: 'ThanhTien',
      key: 'ThanhTien',
      width: 120,
      align: 'right',
      render: (value) => value?.toLocaleString('vi-VN') + ' đ',
    },
    {
      title: 'Cửa hàng',
      dataIndex: 'TenCuaHang',
      key: 'TenCuaHang',
      width: 200,
    },
    {
      title: 'Ngày xuất',
      dataIndex: 'NgayXuat',
      key: 'NgayXuat',
      width: 140,
      render: (value) => moment(value).format('DD/MM/YYYY'),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <h1>Báo cáo số lượng sản phẩm bán theo ngày và cửa hàng</h1>

      <Card title="Chọn cửa hàng" style={{ marginBottom: '16px' }}>
        <Input
          placeholder="Tìm kiếm cửa hàng..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: '16px' }}
        />

        <Checkbox
          indeterminate={selectedStores.length > 0 && selectedStores.length < filteredStores.length}
          checked={selectedStores.length === filteredStores.length && filteredStores.length > 0}
          onChange={handleSelectAll}
          style={{ marginBottom: '12px', fontWeight: 'bold' }}
        >
          Chọn tất cả ({selectedStores.length}/{filteredStores.length})
        </Checkbox>

        <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #d9d9d9', padding: '12px', borderRadius: '4px' }}>
          {filteredStores.map(store => (
            <div key={store.IDCH} style={{ marginBottom: '8px' }}>
              <Checkbox
                checked={selectedStores.includes(store.IDCH)}
                onChange={() => handleStoreChange(store.IDCH)}
              >
                {store.TenCuaHang} (ID: {store.IDCH})
              </Checkbox>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Chọn khoảng thời gian" style={{ marginBottom: '16px' }}>
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          format="DD/MM/YYYY"
          style={{ width: '100%', maxWidth: '400px' }}
        />
      </Card>

      <Button
        type="primary"
        onClick={fetchProductData}
        loading={loading}
        size="large"
        style={{ marginBottom: '16px', marginRight: '12px' }}
      >
        Truy vấn dữ liệu
      </Button>

      <Button
        type="default"
        icon={<FileExcelOutlined />}
        onClick={exportToExcel}
        disabled={productData.length === 0}
        size="large"
        style={{ marginBottom: '16px', backgroundColor: '#52c41a', color: 'white', borderColor: '#52c41a' }}
      >
        Xuất Excel
      </Button>

      <Card title={`Kết quả (${productData.length} bản ghi)`}>
        <Table
          columns={columns}
          dataSource={productData}
          rowKey={(record, index) => `${record.MaSP}-${record.MaCH_CT}-${index}`}
          loading={loading}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showTotal: (total) => `Tổng ${total} bản ghi`,
          }}
          scroll={{ x: 1400 }}
        />
      </Card>
    </div>
  );
};

export default XuatExcelHoaDon;
